/**
 * Asset Filters Component
 * Provides filtering controls for asset list
 */

import { useState, useEffect } from 'react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { X, Search } from 'lucide-react';
import { useCategoryStore, useSubcategoryStore } from '../../stores/categories';
import { useDebounce } from '../../hooks/useDebounce';
import type { AssetListFilter, LifecycleStatus, DispositionStatus, StreetAddress } from '../../types/api';

interface AssetFiltersProps {
  filters: AssetListFilter;
  onFiltersChange: (filters: AssetListFilter) => void;
}

const lifecycleStatuses: LifecycleStatus[] = [
  'Inventoried',
  'Valued',
  'Listed for Sale',
  'Under Offer',
  'Sold',
  'Payment Pending',
  'Delivered',
  'Disposed',
  'Archived',
];

const dispositionStatuses: DispositionStatus[] = [
  'Sell',
  'Keep',
  'Give Away',
  'Donate',
  'Other',
];

const streetAddresses: StreetAddress[] = [
  '30 Light St',
  '28 Light St',
  '15 Templar',
  '30 Workshop',
  'Other',
];

export function AssetFilters({ filters, onFiltersChange }: AssetFiltersProps) {
  const [searchInput, setSearchInput] = useState(filters.search || '');
  const [selectedCategory, setSelectedCategory] = useState<string | undefined>(
    filters.category_id?.toString()
  );

  const debouncedSearch = useDebounce(searchInput, 300);

  useCategoryStore.live();
  const categories = useCategoryStore(s => s.categories);

  const setCategoryId = useSubcategoryStore(s => s.setCategoryId);
  useEffect(() => { setCategoryId(selectedCategory ? parseInt(selectedCategory, 10) : undefined); }, [selectedCategory, setCategoryId]);
  useSubcategoryStore.live();
  const subcategories = useSubcategoryStore(s => s.subcategories);

  useEffect(() => {
    if (debouncedSearch !== filters.search) {
      onFiltersChange({
        ...filters,
        search: debouncedSearch || undefined,
        offset: 0,
      });
    }
  }, [debouncedSearch]);

  const handleCategoryChange = (value: string) => {
    if (value === 'all') {
      setSelectedCategory(undefined);
      onFiltersChange({
        ...filters,
        category_id: undefined,
        subcategory_id: undefined,
        offset: 0,
      });
    } else {
      setSelectedCategory(value);
      onFiltersChange({
        ...filters,
        category_id: parseInt(value, 10),
        subcategory_id: undefined,
        offset: 0,
      });
    }
  };

  const handleSubcategoryChange = (value: string) => {
    onFiltersChange({
      ...filters,
      subcategory_id: value === 'all' ? undefined : parseInt(value, 10),
      offset: 0,
    });
  };

  const handleLifecycleStatusChange = (value: string) => {
    onFiltersChange({
      ...filters,
      lifecycle_status: value === 'all' ? undefined : (value as LifecycleStatus),
      offset: 0,
    });
  };

  const handleDispositionStatusChange = (value: string) => {
    onFiltersChange({
      ...filters,
      disposition_status: value === 'all' ? undefined : (value as DispositionStatus),
      offset: 0,
    });
  };

  const handleStreetAddressChange = (value: string) => {
    onFiltersChange({
      ...filters,
      street_address: value === 'all' ? undefined : (value as StreetAddress),
      offset: 0,
    });
  };

  const clearFilters = () => {
    setSearchInput('');
    setSelectedCategory(undefined);
    onFiltersChange({
      limit: filters.limit,
      offset: 0,
    });
  };

  const activeFilterCount = [
    filters.category_id,
    filters.subcategory_id,
    filters.lifecycle_status,
    filters.disposition_status,
    filters.street_address,
    filters.search,
  ].filter(Boolean).length;

  return (
    <Card>
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">Filters</CardTitle>
          {activeFilterCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              className="h-8 px-2 text-xs"
            >
              <X className="mr-1 h-3 w-3" />
              Clear ({activeFilterCount})
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Search */}
        <div className="space-y-2">
          <Label htmlFor="search">Search</Label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              id="search"
              type="text"
              placeholder="Search assets..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        {/* Category */}
        <div className="space-y-2">
          <Label htmlFor="category">Category</Label>
          <Select
            value={selectedCategory || 'all'}
            onValueChange={handleCategoryChange}
          >
            <SelectTrigger id="category">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map((cat) => (
                <SelectItem key={cat.category_id} value={cat.category_id.toString()}>
                  {cat.category_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Subcategory */}
        {selectedCategory && (
          <div className="space-y-2">
            <Label htmlFor="subcategory">Subcategory</Label>
            <Select
              value={filters.subcategory_id?.toString() || 'all'}
              onValueChange={handleSubcategoryChange}
            >
              <SelectTrigger id="subcategory">
                <SelectValue placeholder="All Subcategories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Subcategories</SelectItem>
                {subcategories.map((sub) => (
                  <SelectItem key={sub.subcategory_id} value={sub.subcategory_id.toString()}>
                    {sub.subcategory_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Lifecycle Status */}
        <div className="space-y-2">
          <Label htmlFor="lifecycle">Lifecycle Status</Label>
          <Select
            value={filters.lifecycle_status || 'all'}
            onValueChange={handleLifecycleStatusChange}
          >
            <SelectTrigger id="lifecycle">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {lifecycleStatuses.map((status) => (
                <SelectItem key={status} value={status}>
                  {status}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Disposition Status */}
        <div className="space-y-2">
          <Label htmlFor="disposition">Disposition</Label>
          <Select
            value={filters.disposition_status || 'all'}
            onValueChange={handleDispositionStatusChange}
          >
            <SelectTrigger id="disposition">
              <SelectValue placeholder="All Dispositions" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Dispositions</SelectItem>
              {dispositionStatuses.map((status) => (
                <SelectItem key={status} value={status}>
                  {status}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Location */}
        <div className="space-y-2">
          <Label htmlFor="location">Location</Label>
          <Select
            value={filters.street_address || 'all'}
            onValueChange={handleStreetAddressChange}
          >
            <SelectTrigger id="location">
              <SelectValue placeholder="All Locations" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Locations</SelectItem>
              {streetAddresses.map((address) => (
                <SelectItem key={address} value={address}>
                  {address}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardContent>
    </Card>
  );
}
